import React from 'react';
import { Icon } from '@iconify/react';
import type { MappedLeadData } from '../../types';
import { Card, CardHeader, SectionLabel, SimpleTabContent } from '../common/Atomic';
import { buildCompanyTextGroups, mergeCompanySummaries, toDisplayItems, uniqueItems } from './companyInsights';

interface PainsChallengesTabProps {
    data: MappedLeadData;
}

type PainCategory = 'critical' | 'constraint' | 'challenge' | 'customer';

const PAIN_CATEGORY_ORDER: PainCategory[] = ['critical', 'constraint', 'challenge', 'customer'];

const PAIN_CATEGORY_META: Record<PainCategory, {
    label: string;
    icon: string;
    accentClass: string;
    borderClass: string;
    iconClass: string;
    badgeClass: string;
    dotClass: string;
}> = {
    critical: {
        label: 'Critical risk',
        icon: 'bx:shield-x',
        accentClass: 'text-rose-200',
        borderClass: 'border-rose-300/15',
        iconClass: 'bg-rose-300/[0.08] text-rose-200',
        badgeClass: 'border-rose-300/15 bg-rose-300/[0.06] text-rose-100/75',
        dotClass: 'bg-rose-300',
    },
    constraint: {
        label: 'Constraint',
        icon: 'bx:time-five',
        accentClass: 'text-amber-200',
        borderClass: 'border-amber-300/15',
        iconClass: 'bg-amber-300/[0.08] text-amber-200',
        badgeClass: 'border-amber-300/15 bg-amber-300/[0.06] text-amber-100/75',
        dotClass: 'bg-amber-300',
    },
    challenge: {
        label: 'Challenge',
        icon: 'bx:target-lock',
        accentClass: 'text-sky-200',
        borderClass: 'border-sky-300/15',
        iconClass: 'bg-sky-300/[0.08] text-sky-200',
        badgeClass: 'border-sky-300/15 bg-sky-300/[0.06] text-sky-100/75',
        dotClass: 'bg-sky-300',
    },
    customer: {
        label: 'Customer need',
        icon: 'bx:user-voice',
        accentClass: 'text-violet-200',
        borderClass: 'border-violet-300/15',
        iconClass: 'bg-violet-300/[0.08] text-violet-200',
        badgeClass: 'border-violet-300/15 bg-violet-300/[0.06] text-violet-100/75',
        dotClass: 'bg-violet-300',
    },
};

const getPainCategory = (key: string): PainCategory => {
    const normalizedKey = key.toLowerCase().replace(/[_-]+/g, ' ');
    if (/customer|client|buyer/.test(normalizedKey)) return 'customer';
    if (/compliance|risk|blocker/.test(normalizedKey)) return 'critical';
    if (/constraint|bottleneck|gap|resource need|time constraint/.test(normalizedKey)) return 'constraint';
    return 'challenge';
};

export const PainsChallengesTab: React.FC<PainsChallengesTabProps> = ({ data }) => {
    const companySummary = mergeCompanySummaries(data.rawCompanySummary, data.companySummary);
    const summaryGroups = buildCompanyTextGroups(companySummary?.pain_points);
    const fallbackItems = uniqueItems([
        ...toDisplayItems(companySummary?.pain_points),
        ...toDisplayItems(data.painPoints),
    ]);
    const painGroups = summaryGroups.length > 0
        ? summaryGroups
        : fallbackItems.length > 0
            ? [{ key: 'identified-challenges', label: 'Identified Challenges', items: fallbackItems }]
            : [];
    const totalChallenges = painGroups.reduce((total, group) => total + group.items.length, 0);
    const categorizedGroups = painGroups
        .map((group, sourceIndex) => ({ ...group, category: getPainCategory(group.key), sourceIndex }))
        .sort((first, second) => (
            PAIN_CATEGORY_ORDER.indexOf(first.category) - PAIN_CATEGORY_ORDER.indexOf(second.category)
            || first.sourceIndex - second.sourceIndex
        ));
    const categoryCounts = categorizedGroups.reduce<Record<PainCategory, number>>((counts, group) => {
        counts[group.category] += group.items.length;
        return counts;
    }, { critical: 0, constraint: 0, challenge: 0, customer: 0 });

    return (
        <SimpleTabContent>
            <Card>
                <CardHeader
                    title="Pain Points & Challenges"
                    icon={<Icon icon="bx:target-lock" className="h-5 w-5" aria-hidden="true" />}
                    iconColor="text-sky-300"
                >
                    {totalChallenges > 0 && (
                        <span className="rounded-md border border-white/10 bg-white/[0.035] px-2 py-1 text-[10px] font-bold text-gray-400">
                            {totalChallenges} {totalChallenges === 1 ? 'item' : 'items'}
                        </span>
                    )}
                </CardHeader>

                {categorizedGroups.length > 0 ? (
                    <>
                        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-white/[0.06] py-2.5" aria-label="Issue categories">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">Impact key</span>
                            {PAIN_CATEGORY_ORDER.map((category) => {
                                const count = categoryCounts[category];
                                if (count === 0) return null;
                                const meta = PAIN_CATEGORY_META[category];
                                return (
                                    <span key={category} className="inline-flex items-center gap-1.5 text-[11px] text-gray-400">
                                        <span className={`h-1.5 w-1.5 rounded-full ${meta.dotClass}`} aria-hidden="true" />
                                        {meta.label}
                                        <span className="font-semibold text-gray-300">{count}</span>
                                    </span>
                                );
                            })}
                        </div>

                        <div className={`grid grid-cols-1 gap-3 ${categorizedGroups.length > 1 ? 'xl:grid-cols-2' : ''}`}>
                            {categorizedGroups.map((group) => {
                                const meta = PAIN_CATEGORY_META[group.category];
                                return (
                                    <section key={group.key} className={`min-w-0 rounded-xl border bg-black/10 p-3 ${meta.borderClass}`} aria-label={group.label}>
                                        <div className="mb-2.5 flex min-w-0 items-center justify-between gap-3">
                                            <div className="flex min-w-0 items-center gap-2">
                                                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${meta.iconClass}`}>
                                                    <Icon icon={meta.icon} className="h-3.5 w-3.5" aria-hidden="true" />
                                                </span>
                                                <SectionLabel label={group.label} className={`mb-0 truncate ${meta.accentClass}`} />
                                            </div>
                                            <span className={`shrink-0 rounded-md border px-1.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] ${meta.badgeClass}`}>
                                                {meta.label}
                                            </span>
                                        </div>
                                        <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                                            {group.items.map((item) => (
                                                <li key={item} className="flex min-w-0 items-start gap-2.5 rounded-lg border border-white/[0.035] bg-white/[0.02] px-2.5 py-2 text-[12px] leading-4.5 text-gray-300">
                                                    <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${meta.dotClass}`} aria-hidden="true" />
                                                    <span className="min-w-0 break-words">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </section>
                                );
                            })}
                        </div>
                    </>
                ) : (
                    <div className="rounded-xl border border-dashed border-white/10 px-4 py-8 text-center">
                        <Icon icon="bx:shield-quarter" className="mx-auto h-6 w-6 text-gray-600" aria-hidden="true" />
                        <p className="mt-2 text-[13px] text-gray-500">No pain points or challenges have been identified yet.</p>
                    </div>
                )}
            </Card>
        </SimpleTabContent>
    );
};
